<template>
  <f7-page name="kyc-individual">
    <f7-navbar :title="$t('kyc.individualTitle')" back-link no-shadow />
    <AppLayout>
      <template #main>
        <f7-block v-if="groupId">
          <KycStatusBadge :status="kycStatus" />
        </f7-block>

        <f7-block-title v-if="!isDone">
          {{ $t("kyc.individualSubtitle") }}
        </f7-block-title>

        <f7-block v-if="!isDone" class="kyc-form-block ">
          <p class="kyc-intro">{{ $t("kyc.individualIntro") }}</p>

          <f7-list media-list dividers inset strong>
            <f7-list-input
              :label="$t('kyc.fullName')"
              type="text"
              :placeholder="$t('kyc.fullNamePh')"
              v-model="form.fullName"
              required
            />
            <f7-list-input
              :label="$t('common.dateOfBirth')"
              :type="f7.device.desktop?'datepicker':'date'"
              :readonly="f7.device.desktop"
              :placeholder="$t('common.dateOfBirth')"
              v-model="form.dateOfBirth"
            />
            <f7-list-item
              :title="$t('common.nationality')"
              smart-select
              :smart-select-params="smartSelectParams"
            >
              <select v-model="selectedCountry">
                <optgroup
                  v-for="(countryList, continentName, index) in countries"
                  :label="continentName"
                  :key="index"
                >
                  <option
                    v-for="country in countryList"
                    :key="country.id"
                    :value="country.countryCode2"
                    :data-option-image="`https://flagcdn.com/${country.countryCode2.toLowerCase()}.svg`"
                    width="24"
                  >
                    {{ country.name }}
                  </option>
                </optgroup>
              </select>
              <template #media>
                <img
                  v-if="groupCountry"
                  :src="`https://flagcdn.com/${groupCountry.countryCode2.toLowerCase()}.svg`"
                  width="24"
                  class="country-flag"
                />
              </template>
            </f7-list-item>
            <f7-list-item :title="$t('kyc.idType')"  smart-select :smart-select-params="{ openIn: isSmallScreen ? 'sheet' : 'popover', closeOnSelect: true,swipeToClose:true }">
              <select name="idType" v-model="form.idType">
                <option value="NATIONAL_ID">
                  {{ $t("kyc.idTypeNationalId") }}
                </option>
                <option value="PASSPORT">{{ $t("kyc.idTypePassport") }}</option>
                <option value="DRIVERS_LICENSE">
                  {{ $t("kyc.idTypeDriversLicense") }}
                </option>
                <option value="RESIDENCE_PERMIT">
                  {{ $t("kyc.idTypeResidencePermit") }}
                </option>
                <option value="VOTER_CARD">
                  {{ $t("kyc.idTypeVoterCard") }}
                </option>
              </select>
            </f7-list-item>
            <f7-list-input
              :label="$t('kyc.idNumber')"
              type="text"
              :placeholder="$t('kyc.idNumberPh')"
              v-model="form.idNumber"
              required
            />
            <f7-list-input
              :label="$t('kyc.idExpiryDate')"
              :type="f7.device.desktop?'datepicker':'date'"
              :readonly="f7.device.desktop"
              v-model="form.idExpiryDate"
            />
          </f7-list>

      

          <f7-button
            large
            raised
            round
            fill
            @click="submit"
            :disabled="!canSubmit || submitting"
          >
            {{
              submitting ? $t("common.loading") : $t("kyc.submitVerification")
            }}
          </f7-button>

          <p class="kyc-note">
            {{ $t("kyc.verificationNote") }}
          </p>
        </f7-block>

        <f7-block v-if="isVerified" class="kyc-success">
          <f7-icon f7="checkmark_circle_fill" size="48" color="green" />
          <h3>{{ $t("kyc.verifiedTitle") }}</h3>
          <p>{{ $t("kyc.verifiedMessage") }}</p>
        </f7-block>

        <f7-block v-if="isRejected" class="kyc-rejected">
          <f7-icon f7="xmark_circle_fill" size="48" color="red" />
          <h3>{{ $t("kyc.rejectedTitle") }}</h3>
          <p>{{ kycStatus?.rejectionReason || $t("kyc.rejectedDefault") }}</p>
          <f7-button fill round raised @click="resubmit">{{
            $t("kyc.resubmit")
          }}</f7-button>
        </f7-block>
     <!-- Document Upload Section -->
        <template v-if="!isDone">
          <f7-block-title>{{ $t("kyc.uploadDocumentTitle") }}</f7-block-title>
          <f7-block>
            <p class="kyc-doc-intro">{{ $t("kyc.uploadDocumentIntro") }}</p>
            <KycDocumentCapture
              v-for="docDef in requiredDocumentTypes"
              :key="docDef.type"
              :groupId="groupId"
              :documentType="docDef"
              :issuingCountry="form.nationality"
              @uploaded="handleDocUploaded"
            />
          </f7-block>
        </template>
       
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { reactive, ref, computed, onMounted, watch } from "vue";
import _ from "lodash";
import { f7 } from "framework7-vue";
import { useKycStore } from "../js/pinia-stores/kyc.js";
import { useKeycloakStore } from "../js/pinia-stores/keycloak.js";
import { useLocationStore } from "../js/pinia-stores/location.js";
import { storeToRefs } from "pinia";
import i18n from "../js/i18n.js";
import AppLayout from "../components/common/AppLayout.vue";
import KycStatusBadge from "../components/kyc/KycStatusBadge.vue";
import KycDocumentCapture from "../components/kyc/KycDocumentCapture.vue";
import { useSmallScreen } from '../js/composables/useMediaQuery.js'


const { t } = i18n.global;
const props = defineProps({
  f7route: Object,
  f7router: Object,
});

const kycStore = useKycStore();
const {
  kycStatus,
  loading,
  isVerified,
  isRejected,
  submitIndividualKyc,
  fetchKycStatus,
  fetchDocuments,
} = storeToRefs(kycStore);
const keycloak = useKeycloakStore();
const locationStore = useLocationStore();
const groupId = props.f7route?.params?.groupId || null;
const submitting = ref(false);
const isDone = ref(false);
const isSmallScreen = useSmallScreen();
const country = computed(() => locationStore.country);
const selectedCountry = ref(null);

if (keycloak.authenticated) {
  selectedCountry.value = keycloak?.profile?.countryCode || null;
}

const countries = computed(() => {
  return Array.isArray(country.value)
    ? _.groupBy(country.value, "continentName")
    : {};
});

const groupCountry = computed(() => {
  if (!selectedCountry.value) return null;
  const countryList = locationStore.country || [];
  return countryList.find((c) => c.countryCode2 === selectedCountry.value);
});

// Ensure selectedCountry is set from keycloak profile when location store loads
watch(
  () => locationStore.country,
  (countries) => {
    if (
      countries?.length &&
      !selectedCountry.value &&
      keycloak.profile?.countryCode
    ) {
      const match = countries.find(
        (c) => c.countryCode2 === keycloak.profile.countryCode,
      );
      if (match) {
        selectedCountry.value = match.countryCode2;
      }
    }
  },
  { immediate: true },
);

// Initialize nationality from selectedCountry
const form = reactive({
  fullName: "",
  dateOfBirth: "",
  nationality: selectedCountry.value || "",
  idType: "NATIONAL_ID",
  idNumber: "",
  idExpiryDate: "",
});

const smartSelectParams = computed(() => ({
  openIn: "popup",
  searchbar: true,
  closeOnSelect: true,
  tabletFullScreen: false,
  swipeToClose: true,
  searchbarPlaceholder: t("common.search"),
}));

const ID_TYPE_TO_DOC_TYPE = {
  NATIONAL_ID: "ID_CARD",
  PASSPORT: "PASSPORT",
  DRIVERS_LICENSE: "DRIVERS_LICENSE",
  RESIDENCE_PERMIT: "PROOF_OF_ADDRESS",
  VOTER_CARD: "ID_CARD",
};

const ID_TYPE_TO_LABEL_KEY = {
  NATIONAL_ID: "kyc.idTypeNationalId",
  PASSPORT: "kyc.idTypePassport",
  DRIVERS_LICENSE: "kyc.idTypeDriversLicense",
  RESIDENCE_PERMIT: "kyc.idTypeResidencePermit",
  VOTER_CARD: "kyc.idTypeVoterCard",
};

const canSubmit = computed(() => {
  return form.fullName.trim() && form.nationality && form.idNumber.trim();
});

const requiredDocumentTypes = computed(() => {
  const docType = ID_TYPE_TO_DOC_TYPE[form.idType] || "ID_CARD";
  const labelKey = ID_TYPE_TO_LABEL_KEY[form.idType] || `kyc.idType${form.idType}`;
  return [
    {
      type: docType,
      label: t(labelKey) || form.idType.replace(/_/g, " "),
      description: "",
      frontSide: true,
    },
  ];
});

function handleDocUploaded(doc) {
  const idx = kycStore.kycDocuments.value.findIndex((d) => d.id === doc.id);
  if (idx >= 0) kycStore.kycDocuments.value[idx] = doc;
  else kycStore.kycDocuments.value.push(doc);
}

function resubmit() {
  isDone.value = false;
}

onMounted(() => {
  if (groupId) {
    kycStore
      .fetchKycStatus(groupId)
      .then(() => {
        if (kycStatus.value) {
          isDone.value =
            kycStatus.value.status === "VERIFIED" ||
            kycStatus.value.status === "REJECTED";
          if (kycStatus.value.fullName)
            form.fullName = kycStatus.value.fullName;
          if (kycStatus.value.nationality)
            form.nationality = kycStatus.value.nationality;
          if (kycStatus.value.idType) form.idType = kycStatus.value.idType;
        }
        kycStore.fetchDocuments(groupId).catch(() => {});
      })
      .catch(() => {});
  }
});

async function submit() {
  submitting.value = true;
  try {
    const payload = {
      groupId,
      fullName: form.fullName,
      dateOfBirth: form.dateOfBirth || null,
      nationality: form.nationality,
      idType: form.idType,
      idNumber: form.idNumber,
      idExpiryDate: form.idExpiryDate || null,
    };
    await kycStore.submitIndividualKyc(payload);
    f7.toast.show({ text: t("kyc.submittedToast"), closeTimeout: 2000 });
    isDone.value = true;
  } catch (e) {
    f7.dialog.alert(
      e?.response?.data?.message || e?.message || t("kyc.submitFailed"),
    );
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped>
.kyc-intro {
  font-size: 13px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin: 0 0 12px;
  line-height: 1.6;
}
.kyc-note {
  font-size: 12px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin-top: 12px;
  line-height: 1.5;
}
.kyc-doc-intro {
  font-size: 13px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin: 0 0 16px;
  line-height: 1.6;
}
.kyc-success,
.kyc-rejected {
  text-align: center;
  padding: 24px 16px;
}
.kyc-success h3,
.kyc-rejected h3 {
  margin: 12px 0 8px;
  font-size: 18px;
}
.kyc-success p,
.kyc-rejected p {
  color: var(--f7-on-surface-variant, #8e8e93);
  font-size: 14px;
}
</style>
